import { View } from 'react-native';
import { Image } from 'expo-image';
import TextLato from '../text/text-lato/TextLato';
import { useStore } from 'src/contexts/StoreContext';
import { neutrals } from 'src/theme/colors';
import { observer } from 'mobx-react-lite';
import { Icon } from '../icon/Icon';
import { ICON_NAMES } from '../icon/icon.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { generateTestID } from 'src/helpers/testIDs.helper';
import Button from '../buttons/button/Button';
import { styles } from './LookupChoise.styles';
import { useEffect } from 'react';
import { useFocusContext } from 'src/contexts/FocusContext';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { LookupChoiceProps } from './LookupChoise.types';

export const LookupChoice = observer(
  ({
    style,
    title,
    isActive,
    testID,
    focusId,
    isSuboptimal = false,
    isPriceVisible = false,
    previewImageUrl,
    formattedPrice,
    accessible = true,
    accessibilityRole = 'button',
    accessibilityLabel,
    ...buttonProps
  }: LookupChoiceProps) => {
    const { i18n } = useStore();
    const { focusElement } = useFocusContext();

    useEffect(() => {
      if (isActive) {
        // wait some time to render the toolbar
        setTimeout(() => {
          focusElement(focusId);
        }, 500);
      }
    }, [isActive]);

    return (
      <Button
        testID={testID}
        focusId={focusId}
        style={mergeStyles(styles.containerChoice, isActive && styles.containerChoiceActive, style)}
        focusStyle={styles.containerChoiceFocus}
        innerStyle={styles.containerChoiceInner}
        accessible={accessible}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={accessibilityLabel ?? i18n.t(`actions:select_x`, { x: title })}
        {...buttonProps}
      >
        {() => {
          return (
            <>
              {isSuboptimal && (
                <View
                  style={[styles.subTag, isActive && { top: 2, left: 2 }]}
                  testID={generateTestID(testID, TestIDs.SUBOPTIMAL_QUALITY_ALERT)}
                >
                  <View style={styles.icon}>
                    <Icon name={ICON_NAMES.Alert} color={neutrals.white} />
                  </View>
                  <TextLato style={styles.subTagText}>{i18n.t('lookups:suboptimal')}</TextLato>
                </View>
              )}
              {previewImageUrl && (
                <Image
                  // @ts-ignore
                  crossOrigin="anonymous"
                  source={previewImageUrl}
                  contentFit="cover"
                  style={styles.choiceImage}
                />
              )}

              <View style={styles.choiceDetails}>
                <TextLato
                  style={[styles.choiceText, isActive && styles.choiceTextActive]}
                  testID={generateTestID(testID, TestIDs.TITLE)}
                >
                  {title}
                </TextLato>
                {isPriceVisible && (
                  <TextLato
                    style={[styles.choicePrice, isActive && styles.choicePriceActive]}
                    testID={generateTestID(testID, TestIDs.PRICE)}
                  >
                    {formattedPrice}
                  </TextLato>
                )}
              </View>
            </>
          );
        }}
      </Button>
    );
  },
);

LookupChoice.displayName = 'LookupChoice';
